// sim/dangerField.ts — a per-tick PREDATOR-DANGER cost field for weighted pathing.
//
// PURPOSE. Colony foragers used to react to predators only by SHRINKING their
// forage radius (ecosystemPressure.foragePulledIn halves it). That is avoidance
// by retreat, not by routing. This field makes avoidance LITERAL: it stamps a
// high integer cost over the tiles around each live ambush/aerial predator, with
// a linear distance falloff to 0 at the edge of a threat radius, and exposes it as
// a `PathCostField`. A danger-aware AStarPathing (engine/Pathing.ts) then routes a
// forager AROUND a spider's/frog's/bird's territory when a cheaper detour exists,
// instead of walking straight through it.
//
// SCOPE. Only the TERRITORIAL ambushers + the aerial hunter establish a "zone" a
// forager should skirt: Spider, Frog (ground ambush) and Bird (aerial). Roamers
// that don't hold ground (Rat raider, Beetle scavenger) and the leashed Dragonfly
// are intentionally excluded — they aren't a fixed place on the map to route
// around, and folding them in would just make the whole map expensive. (The set is
// the DANGER_SPECIES table below; trivially extensible.)
//
// DATA-ORIENTED + ALLOCATION-FREE (EFFICIENCY.md §B). Two preallocated typed
// arrays sized to grid.size: the cost per tile and a per-tile GENERATION STAMP.
// Each tick bumps a single `gen` counter and re-stamps only the cells inside some
// predator's box; a cell whose stamp != gen reads as 0 (no clear pass needed for
// the cost array itself). We DO clear the cost array once at construction. The
// per-tick rebuild is O(predators * radius^2) — bounded by a small constant per
// predator, NOT by grid.size — so it scales with predator count, not map area.
//
// DETERMINISM. Pure integer arithmetic, no RNG, fixed iteration order (active-set
// order, then a (y,x) box scan). Overlapping predators combine by MAX (not sum),
// so the field is bounded by `predatorCost` and never turns a region IMPASSABLE —
// A* can always still thread it if that is genuinely the only route.
//
// NOT HASHED — it is a DERIVED CACHE. The field is a pure function of wildlife
// positions/species, which are ALREADY folded into computeEcosystemStateHash via
// the entity walk + the wildlife species column. Folding the field too would be
// redundant and would risk perturbing the hash for a value that carries no new
// state. So computeEcosystemStateHash deliberately ignores it.

import { KIND, SPECIES, DIR, DIR_DX, DIR_DY } from '../types.ts';
import type { System, Tick, Species, Direction, Pathing } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import { GreedyPathing, AStarPathing } from '../engine/Pathing.ts';
import type { PathCostField } from '../engine/Pathing.ts';
import type { WildlifeSystem } from './wildlife.ts';

/**
 * Which species project a danger ZONE foragers should route around — the fixed,
 * territory-holding predators (Spider/Frog ambush + Bird aerial). Indexed by
 * SPECIES.*; 1 = dangerous. Roamers (Rat/Beetle) and the leashed Dragonfly are 0:
 * they aren't a place on the map, so penalising tiles around their wandering would
 * just noise up the whole field without modelling a territory to skirt.
 */
const DANGER_SPECIES: readonly number[] = (() => {
  const t = new Array<number>(6).fill(0);
  t[SPECIES.Spider] = 1;
  t[SPECIES.Bird] = 1;
  t[SPECIES.Frog] = 1;
  return t;
})();

/** Tuning for the danger field (all integer). */
export interface DangerFieldConfig {
  /** Peak extra cost stamped at a predator's own tile (>= 0). Falls to 0 at edge. */
  readonly predatorCost: number;
  /** Chebyshev radius of the danger zone around each predator (>= 0). */
  readonly radius: number;
}

export class DangerField implements System, PathCostField {
  readonly name = 'danger-field';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly wildlife: WildlifeSystem;
  private readonly predatorCost: number;
  private readonly radius: number;

  /** Per-tile extra cost (valid only when stamp[i] === gen for that tile). */
  private readonly cost: Int32Array;
  /** Per-tile generation stamp; a tile read as 0 unless its stamp is current. */
  private readonly stamp: Int32Array;
  /** Reused active-set snapshot (no per-tick allocation). */
  private readonly scratch: Int32Array;
  /** Current generation; bumped each rebuild so last tick's stamps go stale. */
  private gen = 0;

  // --- Debug / test counters (deterministic; NOT folded into any hash). ---
  /** Number of predators that contributed to the field on the last rebuild. */
  lastPredatorCount = 0;
  /** Total tiles stamped on the last rebuild (debug; bounded). */
  lastStampedTiles = 0;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    wildlife: WildlifeSystem,
    config: DangerFieldConfig,
  ) {
    this.entities = entities;
    this.grid = grid;
    this.wildlife = wildlife;
    this.predatorCost = config.predatorCost < 0 ? 0 : config.predatorCost;
    this.radius = config.radius < 0 ? 0 : config.radius;
    const n = grid.size;
    this.cost = new Int32Array(n);
    this.stamp = new Int32Array(n); // 0 everywhere; gen starts at 1 after first bump
    this.scratch = new Int32Array(entities.capacity);
  }

  /**
   * Extra cost of entering tile (x,y) — the PathCostField contract. Returns the
   * stamped danger cost when the tile was touched THIS generation, else 0. O(1),
   * allocation-free; called per neighbour per A* expansion, so it stays a single
   * bounds check + two typed-array reads.
   */
  extraCostAt(x: number, y: number): number {
    if (x < 0 || x >= this.grid.width || y < 0 || y >= this.grid.height) return 0;
    const i = y * this.grid.width + x;
    return (this.stamp[i] as number) === this.gen ? (this.cost[i] as number) : 0;
  }

  /**
   * Rebuild the field from the CURRENT live predator positions. Runs as a System
   * each tick (registered BEFORE the colony movement so foragers see this tick's
   * danger). Allocation-free; bounded by O(predators * radius^2).
   */
  update(_tick: Tick): void {
    const e = this.entities;
    const grid = this.grid;
    const W = grid.width;
    const H = grid.height;
    const r = this.radius;
    const peak = this.predatorCost;

    // New generation: every tile's stamp now reads as stale (⇒ 0) until re-written.
    const gen = ++this.gen;
    let predators = 0;
    let stamped = 0;

    // Nothing to do if the field is effectively disabled — but still bump gen so a
    // stale field from a hot config can't linger.
    if (peak === 0) {
      this.lastPredatorCount = 0;
      this.lastStampedTiles = 0;
      return;
    }

    // Snapshot the active set so the scan is order-stable and despawn-agnostic.
    const active = e.activeView();
    const n = e.aliveCount;
    const scratch = this.scratch;
    for (let i = 0; i < n; i++) scratch[i] = active[i] as number;

    for (let i = 0; i < n; i++) {
      const id = scratch[i] as number;
      if ((e.kind[id] as number) !== KIND.Wildlife) continue;
      const sp = this.wildlife.species[id] as number;
      if ((DANGER_SPECIES[sp] ?? 0) === 0) continue;
      predators++;

      const px = e.posX[id] as number;
      const py = e.posY[id] as number;

      // Bounded (y,x) box scan over the threat radius. Linear Chebyshev falloff:
      // `peak` at the predator's tile, decreasing to a small positive value at the
      // edge (dist === r) and exactly 0 beyond it. Cells COMBINE by MAX so the
      // field is bounded by `peak` and never becomes an impassable wall.
      const minX = px - r < 0 ? 0 : px - r;
      const minY = py - r < 0 ? 0 : py - r;
      const maxX = px + r >= W ? W - 1 : px + r;
      const maxY = py + r >= H ? H - 1 : py + r;

      for (let ty = minY; ty <= maxY; ty++) {
        const dyAbs = ty > py ? ty - py : py - ty;
        for (let tx = minX; tx <= maxX; tx++) {
          const dxAbs = tx > px ? tx - px : px - tx;
          const dist = dxAbs > dyAbs ? dxAbs : dyAbs; // Chebyshev
          if (dist > r) continue; // outside the zone ⇒ no cost
          // Integer linear falloff: (peak * (r + 1 - dist)) / (r + 1), rounded UP
          // so the edge ring (dist === r) still carries a >=1 nudge while dist > r
          // is exactly 0. At dist 0 this is exactly `peak`.
          const num = peak * (r + 1 - dist);
          const denom = r + 1;
          const c = Math.floor((num + denom - 1) / denom); // ceil(num/denom)

          const ti = ty * W + tx;
          if ((this.stamp[ti] as number) !== gen) {
            // First touch this generation: overwrite the stale value.
            this.stamp[ti] = gen;
            this.cost[ti] = c;
            stamped++;
          } else if (c > (this.cost[ti] as number)) {
            // Overlapping predator: keep the MAX (bounded, never additive).
            this.cost[ti] = c;
          }
        }
      }
    }

    this.lastPredatorCount = predators;
    this.lastStampedTiles = stamped;
  }

  /** True if `species` projects a danger zone (test/debug helper). */
  static isDangerSpecies(species: Species): boolean {
    return (DANGER_SPECIES[species as number] ?? 0) === 1;
  }
}

/**
 * HYBRID danger-aware ground pathing — the perf-conscious `Pathing` foragers
 * actually use. Most forage steps are NOWHERE near a predator; running a full
 * (even bounded) A* for every such step roughly DOUBLES the per-tick cost for no
 * behavioural gain. So this wrapper pays the A* cost ONLY when danger is actually
 * relevant to the next move, and falls back to cheap greedy otherwise:
 *
 *   1. Compute the greedy step toward the target (O(8), the original cost).
 *   2. If the forager's CURRENT tile is in a danger zone, OR the greedy step would
 *      ENTER a danger tile (cost > 0), delegate to the bounded WEIGHTED A* so it
 *      routes AROUND the zone.
 *   3. Otherwise return the greedy step unchanged.
 *
 * This keeps the common case at greedy speed while still producing the literal
 * route-around whenever a predator sits on (or beside) the forager's path. Both
 * branches are deterministic (greedy + the deterministic weighted A*), allocation-
 * free, and integer-only. Because the A* uses the SAME danger field, the decision
 * and the route are consistent. Drop-in: it IS a `Pathing`.
 *
 * NOTE on equivalence: this is a HEURISTIC trigger, not identical to always-A*. A
 * predator far off the straight line but on a tile the greedy step happens to skirt
 * won't trip the A* — which is exactly the point (that predator isn't in the way).
 * The route only changes when danger is on or adjacent to the chosen step, which is
 * precisely when a detour matters.
 */
export class DangerAwarePathing implements Pathing {
  private readonly grid: WorldGrid;
  private readonly field: DangerField;
  private readonly greedy: GreedyPathing;
  private readonly astar: AStarPathing;

  /**
   * @param grid          the world grid.
   * @param field         the live danger field (also passed to the A* as its cost
   *                      layer, so trigger + route share one source of truth).
   * @param maxExpansions A* expansion cap (bounds the per-step cost when it fires).
   */
  constructor(grid: WorldGrid, field: DangerField, maxExpansions: number) {
    this.grid = grid;
    this.field = field;
    this.greedy = new GreedyPathing(grid);
    this.astar = new AStarPathing(grid, maxExpansions, field);
  }

  step(fromX: number, fromY: number, toX: number, toY: number): Direction {
    if (fromX === toX && fromY === toY) return DIR.Stay;
    // Already standing in danger ⇒ let A* find the least-cost way out toward goal.
    if (this.field.extraCostAt(fromX, fromY) > 0) {
      return this.astar.step(fromX, fromY, toX, toY);
    }
    const gdir = this.greedy.step(fromX, fromY, toX, toY);
    if (gdir === DIR.Stay) {
      // Greedy is wedged (a wall/cap local minimum). A* can route around it AND
      // around danger — strictly better here, and rare, so the cost is fine.
      return this.astar.step(fromX, fromY, toX, toY);
    }
    // Would the greedy step walk INTO a danger tile? If so, reroute via A*.
    const nx = fromX + (DIR_DX[gdir] as number);
    const ny = fromY + (DIR_DY[gdir] as number);
    if (this.field.extraCostAt(nx, ny) > 0) {
      return this.astar.step(fromX, fromY, toX, toY);
    }
    // Clear ahead: keep the cheap greedy step.
    return gdir;
  }
}
